import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import {
  addMessageGroupMembers,
  createMessageGroup,
  deleteMessageGroup,
  leaveMessageGroup,
  removeMessageGroupMember,
  updateMessageGroup,
  validateGroupNumbers,
} from "../actions/messageGroups.actions";
import { messageGroupsQueryKeys } from "../messageGroups.queryKeys";
import { MessageGroupChannel, MessageGroupMemberInput, MessageGroupMemberType } from "../messageGroups.types";

export const useMessageGroupAdminMutations = (groupUid?: string, companyId?: number | string | null) => {
  const token = sessionStorage.getItem("token");
  const queryClient = useQueryClient();
  const activeCompanyId = useSelector((state: any) => state.userState.activeCompanyId);
  const effectiveCompanyId = companyId ?? activeCompanyId;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: messageGroupsQueryKeys.list(effectiveCompanyId) });
    if (groupUid) queryClient.invalidateQueries({ queryKey: messageGroupsQueryKeys.summary(effectiveCompanyId, groupUid) });
  };

  const createGroup = useMutation({
    mutationFn: (payload: {
      name: string;
      avatar?: string | null;
      description?: string | null;
      channel?: MessageGroupChannel;
      smsFromNumber?: string | null;
      members: MessageGroupMemberInput[];
    }) => createMessageGroup(token, payload, effectiveCompanyId),
    onSuccess: invalidate,
  });

  const validateNumbers = useMutation({
    mutationFn: (numbers: string[]) => validateGroupNumbers(token, numbers, effectiveCompanyId),
  });

  const renameGroup = useMutation({
    mutationFn: (payload: { name?: string; avatar?: string | null; description?: string | null }) =>
      updateMessageGroup(token, groupUid as string, payload, effectiveCompanyId),
    onSuccess: invalidate,
  });

  const addMembers = useMutation({
    mutationFn: (members: MessageGroupMemberInput[]) => addMessageGroupMembers(token, groupUid as string, members, effectiveCompanyId),
    onSuccess: invalidate,
  });

  const removeMember = useMutation({
    mutationFn: (member: { type: MessageGroupMemberType; uid: string }) =>
      removeMessageGroupMember(token, groupUid as string, member, effectiveCompanyId),
    onSuccess: invalidate,
  });

  const leaveGroup = useMutation({
    mutationFn: () => leaveMessageGroup(token, groupUid as string, effectiveCompanyId),
    onSuccess: invalidate,
  });

  const deleteGroup = useMutation({
    mutationFn: () => deleteMessageGroup(token, groupUid as string, effectiveCompanyId),
    onSuccess: invalidate,
  });

  return { createGroup, validateNumbers, renameGroup, addMembers, removeMember, leaveGroup, deleteGroup, effectiveCompanyId };
};
